import React, { useEffect, useRef, useId } from 'react';
import ReactDOM from 'react-dom';

interface ModalProps {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}

const FOCUSABLE_SELECTORS = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

const Modal: React.FC<ModalProps> = ({ title, onClose, children }) => {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    // Store previously focused element so we can restore it on close
    previousFocusRef.current = document.activeElement as HTMLElement;

    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCloseRef.current();
    };

    // Focus trap: cycle through focusable elements within the modal
    const handleTab = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS)
      ).filter(el => !el.closest('[aria-hidden="true"]'));
      if (focusable.length === 0) { event.preventDefault(); return; }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey) {
        if (document.activeElement === first) { event.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    };

    window.addEventListener('keydown', handleEsc);
    window.addEventListener('keydown', handleTab);
    document.body.style.overflow = 'hidden';

    // Move initial focus into the modal
    const firstFocusable = dialogRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTORS);
    requestAnimationFrame(() => firstFocusable?.focus());

    return () => {
      window.removeEventListener('keydown', handleEsc);
      window.removeEventListener('keydown', handleTab);
      document.body.style.overflow = '';
      // Return focus to the element that opened the modal
      previousFocusRef.current?.focus();
    };
  }, []);
  
  const modalContent = (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 animate-fade-in-fast"
      aria-modal="true"
      role="dialog"
      aria-labelledby={titleId}
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-[rgb(10_12_11/0.45)]" aria-hidden="true" />

      <div
        ref={dialogRef}
        className="modal-content relative mx-auto animate-scale-fade-in"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between pl-5 pr-3 py-3 border-b border-rule">
          <h2 id={titleId} className="font-display text-lg font-semibold text-ink pr-4">
            {title}
          </h2>
          <button onClick={onClose} className="icon-btn" aria-label="Schließen">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.75" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-5 overflow-y-auto text-ink max-h-[calc(90vh-4rem)] scrollbar-thin">
          {children}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default Modal;